import { toLocalDay } from '../filterTimeControl/toLocalDay';
import { toLocalTime } from '../filterTimeControl/toLocalTime';
import { parseTestDate } from '../timeUtils/timeUtils';

type DateCompareArgs = {
  validFrom?: string | null;
  validTo?: string | null;
  testDate?: string | null;
  /**
   * `instant` (default) compares the exact points in time. `day` compares calendar days in
   * the shop's time zone, both ends inclusive: use it for values picked with a day-only
   * picker, which Payload stores as 12:00 UTC of the picked day.
   */
  granularity?: 'instant' | 'day';
};

const pad = (value: number) => value.toString().padStart(2, '0');

/**
 * The day the test date string named, as `YYYY-MM-DD`. Read via the same process-zone getters
 * `parseTestDate` used, so it never shifts by a day the way `toLocalDay` would on a UTC server.
 */
const testDateDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const compareByDay = ({ validFrom, validTo, testDate }: DateCompareArgs): boolean => {
  const parsedTestDate = parseTestDate({ testDate });
  const currentDay = parsedTestDate ? testDateDay(parsedTestDate) : toLocalDay(new Date());
  if (!currentDay) {
    return false;
  }

  if (validFrom) {
    const validFromDay = toLocalDay(new Date(validFrom));
    if (!validFromDay || currentDay < validFromDay) {
      return false;
    }
  }

  if (validTo) {
    const validToDay = toLocalDay(new Date(validTo));
    if (!validToDay || currentDay > validToDay) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if a date falls within a specified validity range.
 *
 * @param validFrom - The start date for the validity range. If provided, the date to compare should be equal to or later than this date. If not provided or null, there is no lower limit.
 * @param validTo - The end date for the validity range. If provided, the date to compare should be equal to or earlier than this date. If not provided or null, there is no upper limit.
 * @param testDate - An optional date to check for validity. If not specified, the current date and time are used.
 * @param granularity - `instant` (default) or `day`, see {@link DateCompareArgs.granularity}.
 *
 * @returns {boolean} - Returns true if the specified date (or the current date if not specified) falls within the validity range. Returns true if both `validFrom` and `validTo` are null or unspecified.
 */
export const dateCompare = ({
  validFrom,
  validTo,
  testDate,
  granularity = 'instant',
}: DateCompareArgs): boolean => {
  // Fail early: If both validFrom and validTo are null or undefined, return true immediately
  if (!validFrom && !validTo) {
    return true;
  }

  if (granularity === 'day') {
    return compareByDay({ validFrom, validTo, testDate });
  }

  const currentDate = parseTestDate({ testDate }) || toLocalTime(new Date());
  if (!currentDate) {
    return false;
  }
  const compareVal = currentDate?.getTime();

  if (validFrom) {
    const validFromVal = toLocalTime(new Date(validFrom))?.getTime();
    if (validFromVal === undefined || Number.isNaN(validFromVal) || compareVal < validFromVal) {
      return false;
    }
  }

  if (validTo) {
    const validToVal = toLocalTime(new Date(validTo))?.getTime();
    if (validToVal === undefined || Number.isNaN(validToVal) || compareVal > validToVal) {
      return false;
    }
  }

  return true;
};
